import React, { useState, useEffect, useRef } from "react";
import { get } from "../../../services/api";

const StudentLedgerTab = ({ selectedStudent }) => {
  const [ledgerData, setLedgerData] = useState([]);
  const [totals, setTotals] = useState({
    feeAmount: 0,
    fineAmount: 0,
    paidAmount: 0,
    balance: 0,
  });
  const [studentInfo, setStudentInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");
  const printRef = useRef();

  useEffect(() => {
    const fetchLedger = async () => {
      if (!selectedStudent?.id) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await get(
          `Student/GetStudentLedgerById?studentId=${selectedStudent.id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        let feeRecords = [];

        if (response?.feeDetail) {
          if (Array.isArray(response.feeDetail)) {
            feeRecords = response.feeDetail;
          } else if (typeof response.feeDetail === "object") {
            feeRecords = [response.feeDetail];
          }
        }

        setLedgerData(feeRecords);
        setStudentInfo({
          name: response.studentName || "N/A",
          rollNo: response.rollNo || "N/A",
          className: response.className || "N/A",
          sectionName: response.sectionName || "N/A",
        });

        if (response?.totalAmountDetail) {
          setTotals({
            feeAmount: Number(response.totalAmountDetail.feeAMountTotal || 0),
            fineAmount: Number(response.totalAmountDetail.fineAMountTotal || 0),
            paidAmount: Number(response.totalAmountDetail.paidAmountTotal || 0),
            balance: Number(response.totalAmountDetail.balanceTotal || 0),
          });
        } else {
          const calculated = feeRecords.reduce(
            (acc, item) => ({
              feeAmount: acc.feeAmount + Number(item.feeAmount || 0),
              fineAmount: acc.fineAmount + Number(item.fineAmount || 0),
              paidAmount: acc.paidAmount + Number(item.paidAmount || 0),
              balance: acc.balance + Number(item.balance || 0),
            }),
            { feeAmount: 0, fineAmount: 0, paidAmount: 0, balance: 0 }
          );
          setTotals(calculated);
        }
      } catch (err) {
        console.error("Error fetching ledger:", err);
        setLedgerData([]);
        setStudentInfo(null);
        setTotals({ feeAmount: 0, fineAmount: 0, paidAmount: 0, balance: 0 });
      } finally {
        setLoading(false);
      }
    };

    fetchLedger();
  }, [selectedStudent, token]);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = date.toLocaleString("en-US", { month: "short" });
      const year = date.getFullYear().toString().slice(-2);
      return `${day}-${month}-${year}`;
    } catch (e) {
      return "-";
    }
  };

  const formatAmount = (amt) => {
    const num = Number(amt || 0);
    return num > 0 ? num.toLocaleString() : "-";
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Normal Screen View */}
      <div className="profile-content-area no-print">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h5 className="fw-bold text-primary mb-0">Fee Ledger</h5>
          <button
            onClick={handlePrint}
            className="btn btn-outline-primary btn-sm"
          >
            <i className="bi bi-printer me-2"></i>Print Report
          </button>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table
                className="table table-hover mb-0"
                style={{ fontSize: "14px" }}
              >
                <thead className="bg-light text-muted small text-uppercase">
                  <tr>
                    <th className="ps-4">Voucher Date</th>
                    <th>Due Date</th>
                    <th>Fee Type</th>
                    <th>Voucher No.</th>
                    <th className="text-end">Fee Amount</th>
                    <th className="text-end">Fine</th>
                    <th className="text-end text-success">Paid</th>
                    <th className="text-end pe-4">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {ledgerData.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center text-muted py-5">
                        No fee transactions found
                      </td>
                    </tr>
                  ) : (
                    ledgerData.map((item, index) => (
                      <tr key={index} className="align-middle">
                        <td className="ps-4 fw-medium">
                          {formatDate(item.voucherDate)}
                        </td>
                        <td>{formatDate(item.dueDate)}</td>
                        <td>{item.challanType || "Monthly Fee"}</td>
                        <td className="fw-bold text-primary">
                          {item.voucherNo || "-"}
                        </td>
                        <td className="text-end">
                          {formatAmount(item.feeAmount)}
                        </td>
                        <td className="text-end text-danger">
                          {formatAmount(item.fineAmount)}
                        </td>
                        <td className="text-end text-success fw-bold">
                          {formatAmount(item.paidAmount)}
                        </td>
                        <td className="text-end fw-bold pe-4">
                          {formatAmount(item.balance)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>

                {ledgerData.length > 0 && (
                  <tfoot>
                    <tr className="bg-light fw-bold border-top">
                      <td colSpan="4" className="text-center text-dark">
                        Total
                      </td>
                      <td className="text-end">
                        {formatAmount(totals.feeAmount)}
                      </td>
                      <td className="text-end text-danger">
                        {formatAmount(totals.fineAmount)}
                      </td>
                      <td className="text-end text-success">
                        {formatAmount(totals.paidAmount)}
                      </td>
                      <td className="text-end pe-4">
                        {formatAmount(totals.balance)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Printable Version - Hidden on Screen */}
      <div className="printable-report d-none d-print-block" ref={printRef}>
        <div className="print-container">
          <div className="print-header">
            <div className="header-content">
              <h2 className="report-title">Fee Ledger Statement</h2>
              <div className="report-info">
                <p className="generated-date">
                  Generated on: {new Date().toLocaleDateString("en-GB")}
                </p>
              </div>
            </div>
          </div>

          {studentInfo && (
            <div className="student-info-section">
              <table className="student-info">
                <tbody>
                  <tr>
                    <td>
                      <strong>Student:</strong> {studentInfo.name}
                    </td>
                    <td>
                      <strong>Roll No:</strong> {studentInfo.rollNo}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Class:</strong> {studentInfo.className}
                    </td>
                    <td>
                      <strong>Section:</strong> {studentInfo.sectionName}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          <div className="ledger-table-section">
            <table className="ledger-table">
              <colgroup>
                <col width="11%" /> {/* Voucher Date */}
                <col width="11%" /> {/* Due Date */}
                <col width="20%" /> {/* Fee Type - Extra width for text */}
                <col width="12%" /> {/* Voucher No */}
                <col width="12%" /> {/* Fee Amount */}
                <col width="11%" /> {/* Fine */}
                <col width="12%" /> {/* Paid */}
                <col width="11%" /> {/* Balance */}
              </colgroup>
              <thead>
                <tr>
                  <th>Voucher Date</th>
                  <th>Due Date</th>
                  <th>Fee Type</th>
                  <th>Voucher No</th>
                  <th>Fee Amount</th>
                  <th>Fine</th>
                  <th>Paid</th>
                  <th>Balance</th>
                </tr>
              </thead>
              <tbody>
                {ledgerData.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="no-data">
                      No fee records found
                    </td>
                  </tr>
                ) : (
                  ledgerData.map((item, i) => (
                    <tr key={i}>
                      <td className="text-center date-cell">
                        {formatDate(item.voucherDate)}
                      </td>
                      <td className="text-center date-cell">
                        {formatDate(item.dueDate)}
                      </td>
                      <td className="text-center fee-type">
                        {item.challanType || "Monthly Fee"}
                      </td>
                      <td className="text-center voucher-no">
                        {item.voucherNo || "-"}
                      </td>
                      <td className="text-center amount">
                        {formatAmount(item.feeAmount)}
                      </td>
                      <td className="text-center fine">
                        {formatAmount(item.fineAmount)}
                      </td>
                      <td className="text-center paid">
                        {formatAmount(item.paidAmount)}
                      </td>
                      <td className="text-center balance">
                        {formatAmount(item.balance)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {ledgerData.length > 0 && (
                <tfoot>
                  <tr className="total-row">
                    <td colSpan="4" className="total-label text-center">
                      TOTAL
                    </td>
                    <td className="text-center total-amount">
                      {formatAmount(totals.feeAmount)}
                    </td>
                    <td className="text-center total-fine">
                      {formatAmount(totals.fineAmount)}
                    </td>
                    <td className="text-center total-paid">
                      {formatAmount(totals.paidAmount)}
                    </td>
                    <td className="text-center total-balance">
                      {formatAmount(totals.balance)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <div className="print-footer">
            <div className="footer-content">
              <div className="footer-note">
                <p>This is a computer generated statement</p>
              </div>
            </div>
            {/* <div className="signature-area">
              <div className="signature-line"></div>
              <p>Accountant Signature</p>
            </div> */}
          </div>
        </div>
      </div>

      {/* Print CSS - Optimized for A4 Portrait */}
      <style jsx global>{`
        @media print {
          /* Reset everything */
          body * {
            visibility: hidden;
            margin: 0 !important;
            padding: 0 !important;
          }

          /* Show only printable report */
          .printable-report,
          .printable-report * {
            visibility: visible;
          }

          .printable-report {
            position: fixed;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            margin: 0 !important;
            padding: 0 !important;
            background: white;
            font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
            color: #000;
          }

          .no-print {
            display: none !important;
          }

          /* Page setup - minimum margins for full width */
          @page {
            size: A4 portrait;
            margin: 10mm 8mm 15mm 8mm; /* Small margins on sides */
          }

          /* Container */
          .print-container {
            width: 100%;
            max-width: 100%;
            height: 100%;
            padding: 0;
            margin: 0;
          }

          /* Header */
          .print-header {
            text-align: center;
            margin-bottom: 10px;
            padding-bottom: 8px;
            border-bottom: 1px solid #000;
          }

          .report-title {
            font-size: 18px;
            font-weight: bold;
            margin: 0 0 5px 0;
            color: #000;
          }

          .generated-date {
            font-size: 11px;
            margin: 0;
            color: #666;
          }

          /* Student Info */
          .student-info-section {
            margin: 10px 0 12px 0;
          }

          .student-info {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 5px;
          }

          .student-info td {
            padding: 4px 8px;
            font-size: 12px;
            width: 50%;
          }

          /* Ledger Table */
          .ledger-table-section {
            width: 100%;
            margin: 0;
            padding: 0;
          }

          .ledger-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 10px;
            table-layout: fixed;
            word-wrap: break-word;
            border: 1px solid #000;
          }

          /* Column widths explicitly set */
          .ledger-table col {
            width: auto;
          }

          .ledger-table th {
            background-color: #f2f2f2;
            border: 1px solid #000;
            padding: 8px 4px;
            font-weight: bold;
            text-align: center;
            font-size: 10px;
            height: 32px;
            vertical-align: middle;
          }

          .ledger-table td {
            border: 1px solid #000;
            padding: 6px 4px;
            vertical-align: middle;
            height: 28px;
            overflow: hidden;
            text-overflow: ellipsis;
            text-align: center;
          }

          /* Specific cell styles */
          .date-cell {
            white-space: nowrap;
          }

          .fee-type {
            word-break: break-word;
          }

          .voucher-no {
            font-weight: bold;
          }

          /* Amount styling - centered */
          .amount,
          .fine,
          .paid,
          .balance {
            font-family: "Courier New", monospace;
            text-align: center;
          }

          .fine {
            color: #d32f2f;
          }

          .paid {
            color: #2e7d32;
            font-weight: bold;
          }

          .balance {
            font-weight: bold;
          }

          /* Row styling */
          .ledger-table tbody tr:nth-child(even) {
            background-color: #f9f9f9;
          }

          /* Total row - centered */
          .total-row {
            background-color: #e8e8e8 !important;
            font-weight: bold;
            border-top: 2px solid #000;
          }

          .total-label {
            font-size: 11px;
            padding: 8px 4px !important;
          }

          .total-amount,
          .total-fine,
          .total-paid,
          .total-balance {
            font-size: 11px;
            padding: 8px 4px !important;
            font-family: "Courier New", monospace;
            text-align: center;
          }

          .total-fine {
            color: #d32f2f;
          }

          .total-paid {
            color: #2e7d32;
          }

          /* Text alignment utility classes */
          .text-center {
            text-align: center !important;
          }

          /* No data */
          .no-data {
            text-align: center;
            padding: 20px !important;
            font-style: italic;
            color: #666;
          }

          /* Footer - adjusted signature position */
          .print-footer {
            margin-top: 40px; /* Increased margin to move signature down */
            padding-top: 15px;
            border-top: 1px solid #000;
          }

          .footer-content {
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
          }

          .signature-area {
            width: 40%;
            text-align: right;
          }

          .signature-line {
            border-bottom: 1px solid #000;
            width: 150px;
            margin-bottom: 5px;
            margin-left: auto;
            margin-right: 0;
          }

          .signature-area p {
            font-size: 10px;
            margin: 0;
            text-align: center;
          }

          .footer-note p {
            font-size: 9px;
            margin: 0;
            color: #666;
            font-style: italic;
          }

          /* Ensure no overflow */
          * {
            box-sizing: border-box;
          }

          /* Force full width */
          html,
          body {
            width: 100% !important;
            overflow: visible !important;
          }
        }

        /* Screen preview */
        @media screen {
          .printable-report {
            display: none;
          }
        }
      `}</style>
    </>
  );
};

export default StudentLedgerTab;
